import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { User, AuthState, OnboardingData, LoginCredentials, OTPCredentials } from '../types/auth';
import { storage } from '../utils/storage';
import { getValidAuthToken, refreshBackendSession } from '../utils/authSession';
import { buildApiUrl } from '../config/env';
import { readResponseJson } from '../lib/api/http';
import * as PhoneAuth from '../services/phoneAuth';
import * as NotificationService from '../services/notifications.native';

const FIND_FRIENDS_COMPLETED_KEY = '@lnb_find_friends_completed';

interface AuthContextType extends AuthState {
  sendOTP: (credentials: LoginCredentials) => Promise<void>;
  verifyOTP: (credentials: OTPCredentials) => Promise<User>;
  completeOnboarding: (data: OnboardingData) => Promise<void>;
  logout: () => Promise<void>;
  reloadUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });
  
  // Store Firebase confirmation for OTP verification (platform-agnostic)
  const [firebaseConfirmation, setFirebaseConfirmation] = useState<any | null>(null);

  /**
   * Helper function to sync FCM token with backend
   * @param shouldRequestPermission - If true, will request permission if not granted (use on login only)
   *                                  If false, will only sync if permission already granted (use on app launch)
   */
  const syncFCMToken = async (shouldRequestPermission = false) => {
    // Skip FCM on web for now
    if (Platform.OS === 'web') {
      console.log('🌐 Web platform - skipping FCM token sync');
      return;
    }
    
    try {
      // Check if user has notification permission
      const hasPermission = await NotificationService.checkNotificationPermission();
      
      if (!hasPermission) {
        if (shouldRequestPermission) {
          // Only request permission if explicitly told to (e.g., during login)
          console.log('📵 No notification permission, requesting...');
          const notifResult = await NotificationService.requestNotificationPermission();
          
          if (!notifResult.granted) {
            console.log('⚠️ User denied notification permission');
            return;
          }
        } else {
          // Don't ask for permission on app launch - that's annoying!
          console.log('📵 No notification permission (not requesting - user must grant in settings)');
          return;
        }
      }
      
      // Get current FCM token
      const fcmToken = await NotificationService.getFCMToken();
      
      if (!fcmToken) {
        console.warn('⚠️ Failed to get FCM token');
        return;
      }
      
      console.log('🔔 FCM Token obtained:', fcmToken.substring(0, 20) + '...');
      
      // Send to backend
      const token = await storage.getToken();
      if (!token) {
        console.warn('⚠️ No auth token, cannot sync FCM token');
        return;
      }
      
      const response = await fetch(buildApiUrl('api/users/fcm-token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ fcmToken }),
      });
      
      const data = await readResponseJson(response);
      
      if (response.ok && data.success) {
        console.log('✅ FCM token synced with backend');
      } else {
        console.warn('⚠️ Failed to sync FCM token:', data.message);
      }
    } catch (error) {
      console.error('❌ Error syncing FCM token:', error);
    }
  };

  // Load user from storage on app start
  useEffect(() => {
    loadUser();
    
    // Firebase ID token refresh is not logout. Keep the backend JWT in sync.
    const unsubscribeIdToken = PhoneAuth.onIdTokenChanged(async (firebaseUser) => {
      if (!firebaseUser) {
        return;
      }
      try {
        await getValidAuthToken();
      } catch (error) {
        console.warn('⚠️ Failed to sync backend token after Firebase ID token change:', error);
      }
    });
    
    // Setup FCM token refresh listener
    const unsubscribeTokenRefresh = NotificationService.onTokenRefresh(async (newToken) => {
      console.log('🔄 FCM Token refreshed:', newToken.substring(0, 20) + '...');
      
      // Sync new token with backend
      try {
        const token = await storage.getToken();
        if (token) {
          await fetch(buildApiUrl('api/users/fcm-token'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ fcmToken: newToken }),
          });
          console.log('✅ Refreshed FCM token synced with backend');
        }
      } catch (error) {
        console.error('❌ Error syncing refreshed FCM token:', error);
      }
    });
    
    return () => {
      unsubscribeIdToken();
      unsubscribeTokenRefresh();
    };
  }, []);

  const loadUser = async () => {
    console.log('🔄 Loading user...');
    try {
      const firebaseUser = await PhoneAuth.waitForAuthReady();
      console.log('🔥 Firebase auth ready:', firebaseUser ? firebaseUser.phoneNumber : 'signed out');

      let token = await getValidAuthToken();
      console.log('🔑 Token:', token ? 'exists' : 'not found');

      // Cached JWT missing/expired is not logout if Firebase still has a user.
      if (!token && firebaseUser) {
        token = await refreshBackendSession();
      }

      if (!token && !firebaseUser) {
        console.log('❌ No Firebase session and no auth token');
        setAuthState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
        });
        return;
      }

      if (!token) {
        const storedUser = await storage.getUser();
        console.warn('⚠️ Firebase user exists but backend token refresh failed; using stored profile');
        setAuthState({
          user: storedUser,
          isLoading: false,
          isAuthenticated: !!storedUser,
        });
        return;
      }

      // Fetch fresh user data from backend with timeout
      try {
        const apiUrl = buildApiUrl('api/users/me');
        console.log('📡 Fetching user from:', apiUrl);
        console.log('⏱️ Starting fetch with 5-second timeout...');

        const fetchCurrentUser = async (authToken: string) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => {
            console.log('⏰ TIMEOUT! Aborting fetch after 5 seconds');
            controller.abort();
          }, 5000);

          const fetchStartTime = Date.now();
          try {
            const result = await fetch(apiUrl, {
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              signal: controller.signal,
            });
            console.log(`📡 Response received in ${Date.now() - fetchStartTime}ms, status:`, result.status);
            return result;
          } finally {
            clearTimeout(timeoutId);
          }
        };

        let response = await fetchCurrentUser(token);
        if (response.status === 401) {
          console.warn('🔒 Auth token rejected, attempting one session refresh');
          const refreshedToken = await refreshBackendSession();
          if (refreshedToken) {
            response = await fetchCurrentUser(refreshedToken);
          }
        }

        if (response.ok) {
          const data = await readResponseJson(response);
          console.log('📸 [AuthContext] User data from backend:', JSON.stringify(data.user, null, 2));
          console.log('📸 [AuthContext] Photo field:', data.user.photo);
          console.log('📸 [AuthContext] Industry field:', data.user.industry);
          console.log('📸 [AuthContext] Designation field:', data.user.designation);
          console.log('📸 [AuthContext] Profession field:', data.user.profession);
          
          if (data.success && data.user) {
            // Map backend user to frontend User type
            const user: User = {
              userId: data.user.id,
              phoneNumber: data.user.phone,
              authProvider: 'phone',
              createdAt: new Date(data.user.createdAt),
              name: data.user.name,
              profession: data.user.profession,
              industry: data.user.industry,
              city: data.user.city,
              photo: data.user.photo, // ← ADD THIS LINE
              previewImageUrl: data.user.previewImageUrl, // Gold card preview for sharing
              onboardingCompleted: data.user.onboardingCompleted,
              role: data.user.role,
              // Contact fields
              email: data.user.email,
              dateOfBirth: data.user.dateOfBirth,
              company: data.user.company,
              designation: data.user.designation,
              gender: data.user.gender,
              country: data.user.country,
              state: data.user.state,
              // Personal fields
              spouse: data.user.spouse,
              children: data.user.children,
              animals: data.user.animals,
              hobbies: data.user.hobbies,
              activityOfInterest: data.user.activityOfInterest,
              cityOfResidence: data.user.cityOfResidence,
              burningDesire: data.user.burningDesire,
              somethingNoOneKnows: data.user.somethingNoOneKnows,
              keyToSuccesses: data.user.keyToSuccesses,
              personnelGoals: data.user.personnelGoals,
              personnelAccomplishments: data.user.personnelAccomplishments,
              personnelInterests: data.user.personnelInterests,
              personnelNetwork: data.user.personnelNetwork,
              personnelSkills: data.user.personnelSkills,
              // Business fields
              businessName: data.user.businessName,
              businessLocation: data.user.businessLocation,
              previousTypeOfJobs: data.user.previousTypeOfJobs,
              businessGoals: data.user.businessGoals,
              businessAccomplishments: data.user.businessAccomplishments,
              businessInterests: data.user.businessInterests,
              businessNetwork: data.user.businessNetwork,
              businessSkills: data.user.businessSkills,
              // Business Details Page fields
              businessPhoto: data.user.businessPhoto,
              businessTitle: data.user.businessTitle,
              businessIndustry: data.user.businessIndustry,
              businessType: data.user.businessType,
              businessCountry: data.user.businessCountry,
              businessPlace: data.user.businessPlace,
              productsServices: data.user.productsServices,
              noOfEmployees: data.user.noOfEmployees,
              businessMobileNumber: data.user.businessMobileNumber,
              linkedinId: data.user.linkedinId,
              companyEmail: data.user.companyEmail,
              businessWebsite: data.user.businessWebsite,
              shareValue: data.user.shareValue,
              businessLink: data.user.businessLink,
              businessLocationFull: data.user.businessLocationFull,
              aboutCompany: data.user.aboutCompany,
              // Social Links
              socialLinks: data.user.socialLinks,
            };

            console.log('✅ [AuthContext] Mapped user object:', JSON.stringify(user, null, 2));
            console.log('📸 [AuthContext] User photo in mapped object:', (user as any).photo);

            // Save updated user to storage
            await storage.saveUser(user);

            setAuthState({
              user,
              isLoading: false,
              isAuthenticated: true,
            });
            
            // Sync FCM token on app launch for authenticated users (if permission already granted)
            // This handles cases where:
            // 1. User granted permission in device settings after previously denying
            // 2. Token was refreshed by Firebase
            // 3. App was updated/reinstalled
            // Note: We don't REQUEST permission here - that's annoying!
            // Only sync if user has already granted permission
            syncFCMToken(false).catch(err => {
              console.warn('⚠️ Failed to sync FCM token on app launch (non-critical):', err);
            });
            
            return;
          }
        }

        if (response.status === 401) {
          const errorData = await readResponseJson(response).catch(() => null);
          const stillSignedIn = Boolean(PhoneAuth.getCurrentUser());
          if (stillSignedIn) {
            const storedUser = await storage.getUser();
            console.warn('🔒 Backend still rejected token after refresh, keeping Firebase session:', errorData?.message);
            setAuthState({
              user: storedUser,
              isLoading: false,
              isAuthenticated: !!storedUser,
            });
            return;
          }

          console.warn('🔒 Auth session invalid and Firebase user is gone:', errorData?.message);
          await storage.removeToken();
          await storage.removeUser();
          setAuthState({
            user: null,
            isLoading: false,
            isAuthenticated: false,
          });
          return;
        }

        if (response.status === 403) {
          const errorData = await readResponseJson(response).catch(() => null);
          // Check if it's a deactivation error
          if (errorData?.message?.includes('deactivated')) {
            console.warn('🚫 User account deactivated');
            // Update stored user with deactivated status
            const storedUser = await storage.getUser();
            if (storedUser) {
              const deactivatedUser = { ...storedUser, role: 'deactivated' };
              await storage.saveUser(deactivatedUser);
              setAuthState({
                user: deactivatedUser,
                isLoading: false,
                isAuthenticated: true,
              });
            }
            return;
          }
        }

        // If backend call fails, try to load from storage as fallback
        console.log('⚠️ Backend call failed, loading from storage');
        const storedUser = await storage.getUser();
        console.log('💾 Stored user:', storedUser ? 'found' : 'not found');
        setAuthState({
          user: storedUser,
          isLoading: false,
          isAuthenticated: !!storedUser,
        });
      } catch (fetchError) {
        console.error('❌ Error fetching user from backend:', fetchError);
        // Fallback to storage
        const storedUser = await storage.getUser();
        console.log('💾 Fallback to stored user:', storedUser ? 'found' : 'not found');
        setAuthState({
          user: storedUser,
          isLoading: false,
          isAuthenticated: !!storedUser,
        });
      }
    } catch (error) {
      console.error('❌ Error loading user:', error);
      setAuthState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  };

  // Send OTP to phone number
  const sendOTP = async (credentials: LoginCredentials) => {
    try {
      console.log('📱 Sending Firebase OTP to:', credentials.phoneNumber);
      
      // Send OTP via Firebase
      const result = await PhoneAuth.sendOTP(credentials.phoneNumber);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send OTP');
      }
      
      // Store confirmation for later verification
      if (result.confirmation) {
        setFirebaseConfirmation(result.confirmation);
      }
      
      console.log('✅ Firebase OTP sent successfully');
      
      // Also notify backend (optional - for analytics/logging)
      try {
        const url = buildApiUrl('api/auth/send-otp');
        await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            phone: credentials.phoneNumber,
          }),
        });
      } catch (backendError) {
        console.warn('⚠️ Backend notification failed (non-critical):', backendError);
      }
    } catch (error) {
      console.error('❌ Error sending OTP:', error);
      throw error instanceof Error ? error : new Error('Failed to send OTP. Please try again.');
    }
  };

  // Verify OTP and create user
  const verifyOTP = async (credentials: OTPCredentials) => {
    try {
      console.log('📱 Verifying Firebase OTP for:', credentials.phoneNumber);
      
      if (!firebaseConfirmation) {
        throw new Error('No OTP session found. Please request a new code.');
      }
      
      // Verify OTP with Firebase
      const result = await PhoneAuth.verifyOTP(firebaseConfirmation, credentials.otp);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to verify OTP');
      }
      
      console.log('✅ Firebase OTP verified successfully');
      
      // Get Firebase ID token for backend authentication
      const firebaseToken = await PhoneAuth.getIdToken();
      if (!firebaseToken) {
        throw new Error('Failed to get Firebase authentication token');
      }
      
      // Authenticate with backend using Firebase token
      const apiUrl = buildApiUrl('api/auth/verify-otp');
      console.log('🌐 Calling backend API:', apiUrl);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout (increased from 30)
      
      let data;
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            phone: credentials.phoneNumber,
            otp: credentials.otp,
            firebaseToken, // Send Firebase token to backend for verification
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        
        console.log('📡 Backend response status:', response.status);
        data = await readResponseJson(response);
        console.log('📡 Backend response data:', { success: data.success, hasToken: !!data.token });

        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to verify OTP with backend');
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        console.error('🔴 Fetch error details:', {
          name: fetchError instanceof Error ? fetchError.name : 'Unknown',
          message: fetchError instanceof Error ? fetchError.message : String(fetchError),
          apiUrl,
        });
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          throw new Error('Network request timed out. Please check your internet connection and try again.');
        }
        if (fetchError instanceof Error && fetchError.message === 'Network request failed') {
          throw new Error(`Cannot connect to server at ${apiUrl}. Please check your internet connection.`);
        }
        throw fetchError;
      }

      console.log('✅ Backend authentication successful');

      // Save token to storage
      await storage.saveToken(data.token);

      // Create user object from backend response
      const newUser: User = {
        userId: data.user.id,
        phoneNumber: data.user.phone,
        authProvider: 'phone',
        name: data.user.name,
        createdAt: new Date(data.user.createdAt),
        onboardingCompleted: data.user.onboardingCompleted,
        role: data.user.role,
      };
      
      // Save user to storage
      await storage.saveUser(newUser);
      
      // Update auth state
      setAuthState({
        user: newUser,
        isLoading: false,
        isAuthenticated: true,
      });
      
      // Clear Firebase confirmation
      setFirebaseConfirmation(null);
      
      // Reload full user profile with all fields including photo
      // Do this BEFORE FCM sync to ensure user data is fully loaded
      console.log('🔄 Reloading full user profile after login...');
      let finalUser = newUser; // Start with basic user data
      try {
        await loadUser();
        console.log('✅ User profile reloaded successfully');
        // After loadUser completes, get the updated user from storage
        const updatedUser = await storage.getUser();
        if (updatedUser) {
          finalUser = updatedUser;
        }
      } catch (loadUserError) {
        console.error('⚠️ Failed to reload user profile (non-critical):', loadUserError);
        // Continue anyway - we already have basic user data saved
      }
      
      console.log('✅ Auth state updated - OTP verified successfully');
      console.log('📤 [AuthContext] Returning user data with onboardingCompleted:', finalUser.onboardingCompleted);
      
      // Request notification permissions and sync FCM token (on login only)
      // This is the ONE TIME we ask for permission
      // If user denies, we won't ask again until next login
      // Do this AFTER user is fully loaded to avoid race conditions
      syncFCMToken(true).catch(notifError => {
        console.warn('⚠️ Failed to setup notifications on login (non-critical):', notifError);
      });
      
      // Return the user data so caller can check onboarding status
      return finalUser;
    } catch (error) {
      console.error('❌ Error verifying OTP:', error);
      // Don't clear firebaseConfirmation here - allow retry with same session
      throw error instanceof Error ? error : new Error('Failed to verify OTP. Please try again.');
    }
  };

  // Complete onboarding
  const completeOnboarding = async (data: OnboardingData) => {
    try {
      if (!authState.user) {
        throw new Error('No user found. Please log in again.');
      }

      const token = await storage.getToken();
      if (!token) {
        throw new Error('No auth token found. Please log in again.');
      }

      let photoUrl: string | undefined = undefined;

      // Upload photo if provided
      if (data.photo) {
        try {
          const formData = new FormData();
          
          // Create file object from URI
          const filename = data.photo.split('/').pop() || 'photo.jpg';
          const match = /\.(\w+)$/.exec(filename);
          const type = match ? `image/${match[1]}` : 'image/jpeg';
          
          // Handle web differently
          if (Platform.OS === 'web') {
            const response = await fetch(data.photo);
            const blob = await response.blob();
            const file = new File([blob], filename, { type });
            formData.append('image', file);
          } else {
            formData.append('image', {
              uri: data.photo,
              name: filename,
              type,
            } as any);
          }

          const uploadResponse = await fetch(buildApiUrl('api/upload/image'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: formData,
          });

          const uploadData = await readResponseJson(uploadResponse);

          if (uploadResponse.ok && uploadData.success) {
            photoUrl = uploadData.url;
            console.log('Photo uploaded:', photoUrl);
          } else {
            console.warn('Photo upload failed:', uploadData.message);
          }
        } catch (uploadError) {
          console.error('Error uploading photo:', uploadError);
          // Continue without photo rather than failing completely
        }
      }

      const response = await fetch(buildApiUrl('api/users/complete-onboarding'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: data.name,
          profession: data.profession,
          industry: data.industry,
          city: data.city,
          country: data.country,
          gender: data.gender,
          photo: photoUrl,
        }),
      });

      const responseData = await readResponseJson(response);

      if (!response.ok || !responseData.success) {
        throw new Error(responseData.message || 'Failed to complete onboarding');
      }

      console.log('Onboarding response:', responseData);
      
      // Update user with onboarding data from backend
      const updatedUser: User = {
        ...authState.user,
        name: responseData.user.name,
        profession: responseData.user.profession,
        industry: responseData.user.industry,
        city: responseData.user.city,
        country: responseData.user.country,
        gender: responseData.user.gender,
        photo: responseData.user.photo, // Include photo from backend response
        onboardingCompleted: true,
      };
      
      // Save updated user to storage
      await storage.saveUser(updatedUser);
      
      // Update auth state
      setAuthState({
        user: updatedUser,
        isLoading: false,
        isAuthenticated: true,
      });
      
      console.log('Onboarding completed successfully');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      throw error instanceof Error ? error : new Error('Failed to complete onboarding. Please try again.');
    }
  };

  // Logout
  const logout = async () => {
    try {
      const token = await storage.getStoredToken();
      
      // Delete FCM token
      try {
        await NotificationService.deleteFCMToken();
      } catch (notifError) {
        console.warn('⚠️ Failed to delete FCM token:', notifError);
      }
      
      // Sign out from Firebase
      try {
        await PhoneAuth.signOut();
      } catch (firebaseError) {
        console.warn('⚠️ Firebase sign out failed:', firebaseError);
      }
      
      // Call backend logout endpoint if token exists
      if (token) {
        try {
          await fetch(buildApiUrl('api/auth/logout'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
        } catch (error) {
          console.warn('Backend logout failed, continuing with local logout:', error);
        }
      }

      // Clear local storage
      await storage.removeUser();
      await storage.removeToken();
      
      // Clear FindFriends completion flag
      await AsyncStorage.removeItem(FIND_FRIENDS_COMPLETED_KEY);
      
      setAuthState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
      
      console.log('Logged out successfully');
    } catch (error) {
      console.error('Error logging out:', error);
      throw new Error('Failed to log out. Please try again.');
    }
  };

  const value: AuthContextType = {
    ...authState,
    sendOTP,
    verifyOTP,
    completeOnboarding,
    logout,
    reloadUser: loadUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
